<template>
  <nav
    v-show="!loading"
    class="ca-pesquisa-paginacao px-size-0 py-size-1 rounded-circle"
    aria-label="Paginação dos resultados"
  >
    <button
      v-if="currentPage > 1"
      class="ca-pesquisa-paginacao__nav caption-action rounded-circle gap-size-3 ca-pesquisa-paginacao__nav--anterior"
      @click="$emit('change', currentPage - 1)"
      aria-label="Página anterior"
    >
      <svg
        class="ca-pesquisa-paginacao__chevron"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M14.5 17L9.5 12L14.5 7"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <span class="ca-pesquisa-paginacao__nav-label">Anterior</span>
    </button>

    <div class="ca-pesquisa-paginacao__pages gap-size-2 px-size-2 py-size-0">
      <template v-for="(page, idx) in visiblePages" :key="idx">
        <span
          v-if="page === ELLIPSIS"
          class="ca-pesquisa-paginacao__ellipsis"
          aria-hidden="true"
          >{{ ELLIPSIS }}</span
        >
        <button
          v-else
          class="ca-pesquisa-paginacao__page rounded-circle caption-medium"
          :class="{ 'is-active': page === currentPage }"
          :aria-current="page === currentPage ? 'page' : undefined"
          :aria-label="`Página ${page}`"
          @click="$emit('change', page)"
        >
          {{ page }}
        </button>
      </template>
    </div>

    <button
      v-if="currentPage < totalPages"
      class="ca-pesquisa-paginacao__nav caption-action rounded-circle gap-size-3 ca-pesquisa-paginacao__nav--seguinte"
      @click="$emit('change', currentPage + 1)"
      aria-label="Página seguinte"
    >
      <span class="ca-pesquisa-paginacao__nav-label">Seguinte</span>
      <svg
        class="ca-pesquisa-paginacao__chevron"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M9.5 7L14.5 12L9.5 17"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
  </nav>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  currentPage: { type: Number, default: 1 },
  totalPages: { type: Number, required: true },
  maxVisible: { type: Number, default: 7 },
  loading: { type: Boolean, default: false },
});

defineEmits(["change"]);

const ELLIPSIS = "…";

const range = (start, end) =>
  Array.from({ length: Math.max(end - start + 1, 0) }, (_, i) => start + i);

const visiblePages = computed(() => {
  const total = props.totalPages;
  const current = props.currentPage;
  const max = props.maxVisible;

  if (total <= max) return range(1, total);

  const boundaryCount = 1;
  const siblingCount = max >= 7 ? 1 : 0;

  const startPages = range(1, boundaryCount);
  const endPages = range(total - boundaryCount + 1, total);

  const siblingsStart = Math.max(
    Math.min(
      current - siblingCount,
      total - boundaryCount - siblingCount * 2 - 1,
    ),
    boundaryCount + 2,
  );
  const siblingsEnd = Math.min(
    Math.max(current + siblingCount, boundaryCount + siblingCount * 2 + 2),
    total - boundaryCount - 1,
  );

  return [
    ...startPages,
    siblingsStart > boundaryCount + 2
      ? ELLIPSIS
      : boundaryCount + 1 < total - boundaryCount
        ? boundaryCount + 1
        : null,
    ...range(siblingsStart, siblingsEnd),
    siblingsEnd < total - boundaryCount - 1
      ? ELLIPSIS
      : total - boundaryCount > boundaryCount
        ? total - boundaryCount
        : null,
    ...endPages,
  ].filter((p) => p !== null);
});
</script>

<style scoped lang="scss">
.ca-pesquisa-paginacao {
  display: inline-flex;
  align-items: center;
  background: var(--ca-color-text-inverted);
  overflow: hidden;

  &__pages {
    display: flex;
    align-items: center;
  }

  &__ellipsis {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    width: 40px;
    height: 40px;
    color: var(--ca-color-text-body);
    font-size: 14px;
    line-height: 22px;
    user-select: none;
  }

  &__page {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: none;
    background: transparent;
    color: var(--ca-color-text-body);
    cursor: pointer;
    transition: background 0.15s;

    &.is-active {
      background: var(--ca-color-hover-pressed-inverted-hover-light);
    }

    &:hover:not(.is-active) {
      background: var(--ca-color-hover-pressed-container-high-hover);
    }

    &:focus-visible {
      outline: 1px solid var(--ca-color-feedback-info);
      outline-offset: 2px;
      border-radius: 10px;
    }
  }

  &__nav {
    display: inline-flex;
    align-items: center;
    min-height: 48px;
    border: none;
    background: transparent;
    color: var(--ca-color-theme-primary);
    cursor: pointer;
    transition: background 0.15s;

    &--seguinte {
      padding: var(--size-3) var(--size-4) var(--size-3) var(--size-5);
    }

    &--anterior {
      padding: var(--size-3) var(--size-5) var(--size-3) var(--size-4);
    }

    &:hover {
      background: var(--ca-color-hover-pressed-container-high-hover);
    }

    &:focus-visible {
      outline: 1px solid var(--ca-color-feedback-info);
      outline-offset: 2px;
      border-radius: 10px;
    }
  }

  &__chevron {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    stroke: currentColor;
  }

  @media (max-width: 767px) {
    &__nav-label {
      display: none;
    }

    &__nav {
      &--anterior,
      &--seguinte {
        padding: var(--size-2);
      }
    }

    &__chevron {
      width: 32px;
      height: 32px;
    }
  }
}
</style>
